import { useState } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Send, Clock } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const authHeaders = () => {
  const t = localStorage.getItem("session_token");
  return t ? { Authorization: `Bearer ${t}`, "Content-Type": "application/json" } : { "Content-Type": "application/json" };
};

/**
 * DoctorReview — reusable component for doctor review workflow.
 * Props:
 *   reviewUrl    — PUT endpoint for marking reviewed
 *   sendUrl      — POST endpoint for sending review to nurse
 *   record       — the analysis/session object (must have doctor_reviewed, doctor_review_name, etc.)
 *   onUpdate     — callback after successful review save
 */
export default function DoctorReview({ reviewUrl, sendUrl, record, onUpdate }) {
  const [comment, setComment] = useState(record?.doctor_comment || "");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const isReviewed = record?.doctor_reviewed === true;

  const handleReview = async (reviewed) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}${reviewUrl}`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({ reviewed, comment }),
      });
      if (res.ok) {
        toast.success(reviewed ? "Kontrollitud!" : "Märge eemaldatud");
        onUpdate?.();
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || "Viga salvestamisel");
      }
    } catch {
      toast.error("Võrguühenduse viga");
    }
    setSaving(false);
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await fetch(`${API}${sendUrl}`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ comment }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || "Otsus saadetud õele");
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || "Saatmine ebaõnnestus");
      }
    } catch {
      toast.error("Võrguühenduse viga");
    }
    setSending(false);
  };

  return (
    <div className="mt-4 border border-slate-200 rounded-xl bg-white p-4" data-testid="doctor-review-section">
      <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
        {isReviewed ? (
          <CheckCircle2 className="w-5 h-5 text-emerald-500" />
        ) : (
          <Clock className="w-5 h-5 text-red-500" />
        )}
        Arsti kontroll
      </h3>

      {/* Review status with date/time */}
      {isReviewed ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-3 text-sm" data-testid="review-status-done">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
            <span className="font-medium text-emerald-800">Kontrollitud</span>
          </div>
          <div className="mt-1 text-emerald-600 text-xs pl-6">
            <span>{record.doctor_review_name}</span>
            {record.doctor_review_time && (
              <span className="ml-2">{record.doctor_review_time}</span>
            )}
          </div>
          {record.doctor_comment && (
            <div className="mt-2 pl-6 text-xs text-emerald-700 italic" data-testid="review-comment-display">
              &ldquo;{record.doctor_comment}&rdquo;
            </div>
          )}
        </div>
      ) : (
        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3 text-sm" data-testid="review-status-pending">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-red-500 flex-shrink-0" />
            <span className="font-medium text-red-700">Ootab arsti kontrolli</span>
          </div>
        </div>
      )}

      {/* Comment box */}
      <div className="mb-3">
        <label className="text-xs text-slate-500 mb-1 block">Arsti kommentaar / muudetud otsus / soovitus õele</label>
        <textarea
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-y min-h-[60px] focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
          placeholder="Kirjuta siia, kui otsus on muutunud või on soovitus õele..."
          value={comment}
          onChange={e => setComment(e.target.value)}
          maxLength={2000}
          data-testid="doctor-comment-input"
        />
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        {!isReviewed ? (
          <Button
            size="sm"
            onClick={() => handleReview(true)}
            disabled={saving}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="mark-reviewed-btn"
          >
            <CheckCircle2 className="w-4 h-4 mr-1" />
            {saving ? "Salvestab..." : "Märgi kontrollituks!"}
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={() => handleReview(false)}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="unmark-reviewed-btn"
          >
            <CheckCircle2 className="w-4 h-4 mr-1" />
            {saving ? "Salvestab..." : "Kontrollitud"}
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={handleSend}
          disabled={sending}
          data-testid="send-review-to-nurse-btn"
        >
          <Send className="w-4 h-4 mr-1" />
          {sending ? "Saadab..." : "Saada otsus õele"}
        </Button>
      </div>
    </div>
  );
}
